import { ReactNode } from 'react'
import { Description, DialogPanel, DialogTitle } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import Button from './Button'
import SimpleModal from './SimpleModal'

type Props = {
    open: boolean
    onClose: () => void
    onConfirm: () => void
    /** Sentence case, no trailing full stop. */
    title: string
    /** One or more short paragraphs. Strings render muted; pass elements for emphasis. */
    description?: ReactNode
    confirmLabel?: string
    cancelLabel?: string
    /** Shows a spinner in the confirm button and blocks every way of dismissing. */
    loading?: boolean
    /** Badge icon element (typically a h-6 w-6 heroicon). */
    icon?: ReactNode
    /** Badge colours — override for non-destructive confirms, e.g. 'bg-arwen-100 text-arwen-700'. */
    iconClassName?: string
    /** Extra content below the description (lists, progress, form fields). */
    children?: ReactNode
}

/**
 * The standard Arwen confirmation dialog: icon badge, Jost 500 title, muted
 * description and a Cancel/Confirm pair using the shared Button. Replaces the
 * hand-rolled Tailwind-UI confirm markup that was copy-pasted across delete
 * flows — and unlike those copies it gets focus trapping, Escape handling and
 * transitions from the SimpleModal shell.
 */
export default function ConfirmModal(props: Props) {
    const {
        open,
        onClose,
        onConfirm,
        title,
        description,
        confirmLabel = 'Confirm',
        cancelLabel = 'Cancel',
        loading,
        icon,
        iconClassName = 'bg-red-100 text-red-600',
        children
    } = props

    return (
        <SimpleModal open={open} onClose={loading ? () => {} : onClose}>
            <DialogPanel className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all duration-300">
                <div className="px-6 pb-4 pt-5 sm:flex sm:items-start">
                    <div
                        className={clsx(
                            'mx-auto flex h-12 w-12 flex-none items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10',
                            iconClassName
                        )}
                    >
                        {icon ?? <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />}
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                        {/* font-sans + !font-medium override the global Merriweather/400
                            h1-h4 rule in index.css — app headings are Jost 500. */}
                        <DialogTitle
                            as="h3"
                            className="font-sans !font-medium text-base leading-6 text-ink-900"
                        >
                            {title}
                        </DialogTitle>
                        {description && (
                            <Description as="div" className="mt-2 space-y-4 text-sm text-ink-500">
                                {description}
                            </Description>
                        )}
                        {children}
                    </div>
                </div>
                <div className="flex flex-row-reverse gap-3 bg-ink-50 px-6 py-3">
                    <Button
                        text={confirmLabel}
                        primary={true}
                        onClick={onConfirm}
                        loading={loading}
                        disabled={loading}
                    />
                    <Button
                        text={cancelLabel}
                        primary={false}
                        onClick={onClose}
                        disabled={loading}
                    />
                </div>
            </DialogPanel>
        </SimpleModal>
    )
}
